import { Component, Inject, OnInit, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FieldConfiguration } from '../../models/field-configuration';
import { FieldsConfigurationService } from '../../services/fields-configuration.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PageName } from '@shared/enums/page-name';

@Component({
  selector: 'app-fields-configuration',
  templateUrl: './fields-configuration.component.html',
  styleUrl: './fields-configuration.component.scss'
})
export class FieldsConfigurationComponent implements OnInit, AfterViewInit {

  searchQuery: string = '';
  fieldsConfiguration: FieldConfiguration[] = [];
  initLoading = true;
  saveLoading = false;

  @ViewChildren('fieldRow') fieldRows!: QueryList<ElementRef>;

  matchedIndices: number[] = [];
  translatedFieldNames: string[] = [];

  constructor(
    private fieldsConfigurationService: FieldsConfigurationService,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<FieldsConfigurationComponent>,
    // `labelKeys`: full translation keys by field, for fields whose label is not `${translationKey}.${field}`
    @Inject(MAT_DIALOG_DATA) public data: { pageName: PageName, translationKey: string, labelKeys?: Record<string, string> }
  ) { }

  labelKey(field: FieldConfiguration): string {
    return this.data.labelKeys?.[field.field] ?? `${this.data.translationKey}.${field.field}`;
  }

  async ngOnInit() {
    await this.loadFieldsConfiguration();
    await this.updateTranslatedFieldNames();
    this.initLoading = false;
  }

  ngAfterViewInit() {
    // Listen for changes in the fieldRows (e.g., after search)
    this.fieldRows.changes.subscribe(() => {
      this.scrollToFirstMatch();
    });
  }

  async updateTranslatedFieldNames() {
    // Get translated names for all fields
    const keys = this.fieldsConfiguration.map(f => this.labelKey(f));
    const translations = this.translateService.instant(keys);
    // If get returns an object, map accordingly
    if (Array.isArray(translations)) {
      this.translatedFieldNames = translations;
    } else {
      this.translatedFieldNames = keys.map(k => (translations as any)[k] || k);
    }
    this.updateMatchedIndices();
  }

  async loadFieldsConfiguration() {
    this.fieldsConfiguration = await this.fieldsConfigurationService.getFieldsConfiguration(this.data.pageName);
    await this.updateTranslatedFieldNames();
  }

  onSearchQueryChange() {
    this.updateMatchedIndices();
  }

  updateMatchedIndices() {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.matchedIndices = [];
      return;
    }
    this.matchedIndices = this.translatedFieldNames
      .map((name, i) => ({ name, i }))
      .filter(item => item.name.toLowerCase().includes(query))
      .map(item => item.i);
    // Scroll to first match after updating
    setTimeout(() => this.scrollToFirstMatch(), 0);
  }

  scrollToFirstMatch() {
    if (this.matchedIndices.length > 0 && this.fieldRows) {
      const firstIndex = this.matchedIndices[0];
      const el = this.fieldRows.toArray()[firstIndex]?.nativeElement;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  onReset() {
    this.fieldsConfiguration = this.fieldsConfigurationService.getDefaultConfiguration(this.data.pageName);
    this.updateTranslatedFieldNames();
  }

  async onSubmit() {
    this.saveLoading = true;
    await this.fieldsConfigurationService.saveFieldsConfiguration(this.data.pageName, this.fieldsConfiguration);
    this.dialogRef.close(this.fieldsConfiguration);
  }

  toggleFieldVisibility(field: FieldConfiguration) {
    if (field.canChangeVisibility) {
      field.isVisible = !field.isVisible;
    }
  }

  onDrop(event: CdkDragDrop<FieldConfiguration[]>) {
    moveItemInArray(this.fieldsConfiguration, event.previousIndex, event.currentIndex);

    // Re-pin position-locked fields to the top so other fields can never be reordered
    // above them, regardless of where the user drops them.
    this.fieldsConfiguration.sort((a, b) => {
      if (a.isPositionLocked && !b.isPositionLocked) return -1;
      if (!a.isPositionLocked && b.isPositionLocked) return 1;
      return 0;
    });

    // Update order numbers
    this.fieldsConfiguration.forEach((field, index) => {
      field.order = index + 1;
    });
    this.updateTranslatedFieldNames();
  }

}
